<template>
  <div class="mx-auto my-2">
    <v-card class="mx-4" flat>
      <v-card-title>
        CPD Provider Report
        <v-spacer></v-spacer>

        <div class="ml-12">
          <download-excel
              :data="selected"
              :fields="export_fields"
              name="cpdProviderReport.xls"
              type="csv"
          >
            <v-btn
                class="teal white--text"
                color="primary"
                dark
                depressed
                small
            >
              <v-icon left>mdi-export</v-icon>
              <span>Export</span>
            </v-btn>
          </download-excel>
        </div>
      </v-card-title>

      <v-data-table
          v-model="selected"
          :headers="headers"
          :items="cpdProviderReport.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="cpdProviderReport.count"
          class="elevation-1"
          loading-text="Loading... please wait"
          show-select
      >
        <template v-slot:top>
          <v-layout class="p-3" row wrap>
            <v-flex class="p-3" xs3>
              <v-autocomplete
                  v-model="nameValue"
                  :clearable="clearable"
                  :items="CPD_PROVIDERS"
                  :loading="cpdProviderLoading"
                  item-text="trainingCenterName"
                  item-value="trainingCenterName"
                  label="CPD Provider"
              ></v-autocomplete>
            </v-flex>
            <v-flex class="p-3" xs3>
              <v-autocomplete
                  v-model="cpdOwnershipValue"
                  :clearable="clearable"
                  :items="CPD_PROVIDER_OWNERSHIP"
                  item-text="name"
                  item-value="name"
                  label="CPD Provider's Ownership"
                  return-object
              ></v-autocomplete>
            </v-flex>
            <v-flex class="p-3" xs2>
              <v-autocomplete
                  v-model="regionValue"
                  :clearable="clearable"
                  :items="REGION"
                  :loading="regionLoading"
                  item-text="name"
                  item-value="id"
                  label="Region"
                  return-object
                  @change="regionOnChange(regionValue)"
              ></v-autocomplete>
            </v-flex>
            <v-flex class="p-3" xs2>
              <v-autocomplete
                  v-model="zoneValue"
                  :clearable="clearable"
                  :items="ZONE"
                  :loading="zoneLoading"
                  item-text="name"
                  item-value="id"
                  label="Zone"
                  return-object
                  @change="zoneOnChange(zoneValue)"
              ></v-autocomplete>
            </v-flex>
            <v-flex class="p-3" xs2>
              <v-autocomplete
                  v-model="woredaValue"
                  :clearable="clearable"
                  :items="WOREDA"
                  :loading="woredaLoading"
                  item-text="name"
                  item-value="id"
                  label="Woreda"
                  return-object
              ></v-autocomplete>
            </v-flex>
            <v-flex class="p-3" xs3>
              <v-autocomplete
                  v-model="accreditorValue"
                  :clearable="clearable"
                  :items="ACCREDITED_BY"
                  item-text="accreditorName"
                  item-value="name"
                  label="Accreditor"
                  return-object
              ></v-autocomplete>
            </v-flex>
            <v-flex class="p-3" xs2>
              <v-switch v-model="statusValue" :label="
                `${ statusValue === true
                  ? 'Active'
                  : 'Not Active'
                }`
              " class="black--text"></v-switch>
            </v-flex>
          </v-layout>
        </template>

        <template v-slot:[`item.trainingCenterName`]="{ item }">
          <label for="item">{{ item.trainingCenterName ? item.trainingCenterName : "-" }}</label>
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item.isActive)" dark>
            {{ item.isActive == true ? "Active" : "Not Active" }}
          </v-chip>
        </template>
        <template v-slot:[`item.cpdProviderOwnership`]="{ item }">
          <label for="item">{{ item.cpdProviderOwnership ? item.cpdProviderOwnership : "-" }}</label>
        </template>
        <template v-slot:[`item.woreda.zone.location.name`]="{ item }">
          <label for="item">{{
              item?.region?.name ??
              item?.zone?.location?.name ??
              item?.woreda?.zone?.location.name ?? "-"
            }}</label>
        </template>
        <template v-slot:[`item.woreda.zone.name`]="{ item }">
          <label for="item">{{ item?.zone?.name ?? item?.woreda?.zone?.name ?? "-" }}</label>
        </template>
        <template v-slot:[`item.woreda.name`]="{ item }">
          <label for="item">{{ item?.woreda?.name ?? "-" }}</label>
        </template>
        <template v-slot:[`item.geolocation`]="{ item }">
          <label for="item">{{ item.geolocation ? item.geolocation : "-" }}</label>
        </template>
        <template v-slot:[`item.accreditedBy.name`]="{ item }">
          <label for="item">{{
              item.accreditedBy ? item.accreditedBy.accreditorName : "-"
            }}</label>
        </template>
        <template v-slot:[`item.accreditedBy.dateOfAccreditation`]="{ item }">
          <label for="item">{{
              item.accreditedBy ? item.accreditationDate : "-"
            }}</label>
        </template>
        <template v-slot:[`item.renewal_date`]="{ item }">
          <label for="item">{{
              item.renewalDate ? item.renewalDate : "-"
            }}</label>
        </template>
        <template v-slot:[`item.institutionEmailAddress`]="{ item }">
          <label for="item">{{
              item.institutionEmailAddress ? item.institutionEmailAddress : "-"
            }}</label>
        </template>
        <template v-slot:[`item.coordinatorName`]="{ item }">
          <label for="item">{{
              item.coordinatorName ? item.coordinatorName : "-"
            }}</label>
        </template>
        <template v-slot:[`item.coordinatorEmailAddress`]="{ item }">
          <label for="item">{{
              item.coordinatorEmailAddress ? item.coordinatorEmailAddress : "-"
            }}</label>
        </template>
        <template v-slot:[`item.coordinatorPhoneNumber`]="{ item }">
          <label for="item">{{ item.coordinatorPhoneNumber ? item.coordinatorPhoneNumber : "-" }}</label>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import {getExpertLevels, getRegionList, getUser, getWoredaByZoneId, getZoneByRegionId} from "@/utils/helper";
import {CpdProviderReportService} from "./cpd-provider-report-service";
import {AccreditorService} from '../../Accreditors/accreditor.service'
import {TrainingCenterService} from "@/components/TrainingCenter/training_center.service";

export default {
  data() {
    return {
      search: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      cpdProviderReport: [],
      export_fields: {
        "CPD Provider Name": "trainingCenterName",
        "CPD Provider's Status": {
          field: "isActive",
          callback: (value) => {
            const resp = value !== null ? (value ? 'Active' : 'Not Active') : '-';
            return resp;
          }
        },
        "CPD Provider's Ownership": "cpdProviderOwnership",
        "Region": "region.name",
        "Zone": "woreda.zone.name",
        "Woreda": "woreda.name",
        "Geolocation": "geolocation",
        "Accreditor": "accreditedBy.accreditorName",
        "Accreditation Date": "accreditationDate",
        "Next Renewal Date": "renewalDate",
        "Institution Email Address": "institutionEmailAddress",
        "Coordinator's Name": "coordinatorName",
        "Coordinator's Email Address": "coordinatorEmailAddress",
        "Coordinator's Phone Number": "coordinatorPhoneNumber",
      },
      headers: [
        {text: "CPD Provider Name", value: "trainingCenterName"},
        {text: "CPD Provider's Status", value: "isActive", align: "center"},
        {
          text: "CPD Provider's Ownership",
          value: "cpdProviderOwnership",
          filter: (value) => {
            this.selected = [];
            if (
                this.cpdOwnershipValue == null ||
                this.cpdOwnershipValue == undefined
            )
              return true;
            return value == this.cpdOwnershipValue;
          },
        },

        {
          text: "Region",
          value: "woreda.zone.location.name",
          filter: (value) => {
            this.loading = true;
            this.selected = [];
            if (
                this.regionValue == null ||
                this.regionValue == undefined
            ) {
              this.loading = false;
              return true;
            } else {
              this.loading = false
              return value == this.regionValue.name;
            }
          },
        },
        {
          text: "Zone",
          value: "woreda.zone.name",
          filter: (value) => {
            this.loading = true;
            this.selected = [];
            if (
                this.zoneValue == null ||
                this.zoneValue == undefined
            ) {
              this.loading = false;
              return true;
            } else {
              this.loading = false
              return value == this.zoneValue.name;
            }
          },
        },
        {
          text: "Woreda",
          value: "woreda.name",
          filter: (value) => {
            this.loading = true;
            this.selected = [];
            if (
                this.woredaValue == null ||
                this.woredaValue == undefined
            ) {
              this.loading = false;
              return true;
            } else {
              this.loading = false
              return value == this.woredaValue.name;
            }
          },
        },
        {text: "Geolocation", value: "geolocation"},
        {
          text: "Accreditor",
          value: "accreditedBy.name",
          filter: (value) => {
            this.selected = [];
            if (
                this.accreditorValue == null ||
                this.accreditorValue == undefined
            )
              return true;
            return value == this.accreditorValue;
          },
        },
        {text: "Accreditation Date", value: "accreditedBy.dateOfAccreditation"},
        {text: "Next Renewal Date", value: "renewal_date"},
        {text: "Institution Email Address", value: "institutionEmailAddress"},
        {text: "Coordinator's Name", value: "coordinatorName"},
        {text: "Coordinator's Email Address", value: "coordinatorEmailAddress"},
        {text: "Coordinator's Phone Number", value: "coordinatorPhoneNumber"},
      ],
      loading: false,
      clearable: true,
      selected: [],
      CPD_PROVIDER_OWNERSHIP: [],
      CPD_PROVIDERS: [],
      cpdOwnershipValue: null,
      EXPERT_LEVEL: [],
      expertLevelValue: null,
      REGION: [],
      regionValue: null,
      ZONE: [],
      zoneValue: null,
      WOREDA: [],
      woredaValue: null,
      regionLoading: false,
      zoneLoading: false,
      woredaLoading: false,
      ACCREDITED_BY: [],
      accreditorValue: null,
      nameValue: null,
      statusValue: true,
      cpdProviderLoading: false,
    };
  },
  async beforeMount() {
    this.fetchRegistrationReports();
    this.CPD_PROVIDER_OWNERSHIP = [{
      id: 1,
      name: "Public",
      code: "PUB"
    }, {
      id: 2,
      name: "Private",
      code: "PRI"
    }, {
      id: 3,
      name: "Associations",
      code: "ASS"
    }, {
      id: 4,
      name: "Other",
      code: "OTH"
    }];
    this.EXPERT_LEVEL = await getExpertLevels();
    const accreditorsResp = await AccreditorService.getAllAccreditorsList();
    this.ACCREDITED_BY = accreditorsResp.data.rows;
    this.REGION = await getRegionList();

    this.cpdProviderLoading = true;
    const trainingCenterResp = await TrainingCenterService.getAllTrainingCenters();
    this.CPD_PROVIDERS = trainingCenterResp.data.rows;
    this.cpdProviderLoading = false;

  },
  watch: {
    options: {
      handler() {
        this.fetchRegistrationReports();
      },
      deep: true,
    },
    statusValue: function () {
      this.fetchRegistrationReports();
    },
    nameValue: function (val) {
      this.nameValue = val == '' ? null : val;
      this.fetchRegistrationReports();
    },
    cpdOwnershipValue: function (val) {
      this.cpdOwnershipValue = val == '' ? null : val;
      this.fetchRegistrationReports();
    },
    regionValue: function (val) {
      this.regionValue = val == '' ? null : val;
      this.fetchRegistrationReports();
    },
    zoneValue: function (val) {
      this.zoneValue = val == '' ? null : val;
      this.fetchRegistrationReports();
    },
    woredaValue: function (val) {
      this.woredaValue = val == '' ? null : val;
      this.fetchRegistrationReports();
    },
    accreditorValue: function (val) {
      this.accreditorValue = val == '' ? null : val;
      this.fetchRegistrationReports();
    },
  },
  computed: {
    canViewRegistration() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "CPD Provider Report"
      );
    },
  },
  methods: {
    async epxertLevelChange(value) {
      this.regionLoading = true;
      if (value && value.id == 2) {
        this.REGION = await getRegionList();

        this.regionLoading = false;
      } else {
        this.REGION = [];
        this.WOREDA = [];
        this.ZONE = [];

        this.regionLoading = false;
      }
    },
    async regionOnChange(event) {
      this.zoneLoading = true;
      this.ZONE = [];
      this.WOREDA = [];
      if (event) {
        const regionId = event.id;
        this.ZONE = await getZoneByRegionId(regionId);
      }
      this.zoneLoading = false;
    },
    async zoneOnChange(event) {
      this.woredaLoading = true;
      this.WOREDA = [];
      if (event) {
        const zoneId = event.id;
        this.WOREDA = await getWoredaByZoneId(zoneId);
      }
      this.woredaLoading = false;
    },
    async fetchRegistrationReports() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      let searchParams = '?';
      itemsPerPage > 0 && (searchParams += `page=${page}&pageSize=${itemsPerPage}`);

      this.cpdOwnershipValue !== null && (searchParams += '&cpdProviderOwnership=' + this.cpdOwnershipValue.name);
      this.regionValue !== null && (searchParams += '&regionId=' + this.regionValue.id);
      this.zoneValue !== null && (searchParams += '&zoneId=' + this.zoneValue.id);
      this.woredaValue !== null && (searchParams += '&woredaId=' + this.woredaValue.id);
      this.accreditorValue !== null && (searchParams += '&accreditorId=' + this.accreditorValue.id);
      this.nameValue !== null && (searchParams += '&name=' + this.nameValue);
      this.statusValue !== null && (searchParams += '&status=' + this.statusValue);

      const res = await CpdProviderReportService.getCpdProviderReportsWP(searchParams);
      if (res.status === 200) {
        this.loading = false;
        this.cpdProviderReport = res.data;
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching cpd provider reports, Please try Again!"
        );
      }
    },
    getColor(isActive) {
      if (isActive == false) return "red";
      else return "green";
    },
  },
};

</script>

<style scoped>
.addCard {
  background: hsla(0, 0%, 100%, 0.45);
}
</style>